import React, { useCallback, useEffect, useState } from 'react';
import { SwitchComponent } from '../FormComponent';
import apiClient from '../../api/axiosInstance';
import styles from './NotificationSettingsTable.module.scss';

const NOTIFICATION_SETTINGS_LABELS = {
  heading: 'Notification preferences',
  description: 'Choose how you receive each notification.',
  event: 'Event',
  loading: 'Loading notification preferences…',
  empty: 'No notification preferences are available for your role.',
  loadError: 'Unable to load notification preferences.',
  updateError: 'Unable to update this notification preference.',
  retry: 'Try again',
  unavailable: '—',
  channels: {
    in_app: 'In-app',
    email: 'Email',
    // webhook: 'Webhook', // Removed: webhook is project-scoped (see project webhook settings), not a per-user toggle here
  },
  events: {
    project_member_added: 'Project member added',
    project_member_removed: 'Project member removed',
    test_launch_finished: 'Test launch finished',
    // ai_cost_threshold: 'AI cost threshold reached', // Disabled: hides "Cost & Governance" row from settings tab
    webhook_delivery_failed: 'Webhook delivery failed',
  },
};

const CHANNELS = Object.keys(NOTIFICATION_SETTINGS_LABELS.channels);

const getPreferenceKey = (eventKey, channel) => `${eventKey}:${channel}`;

const groupEventsByCategory = (events) => events.reduce((categories, event) => {
  const category = event.category || '';
  if (!categories[category]) categories[category] = [];
  categories[category].push(event);
  return categories;
}, {});

const getResponseData = (response) => response?.data?.data ?? [];

const getPreferencesPath = (projectId) => `/notification/projects/${projectId}/preferences`;

const NotificationSettingsTable = ({ projectId }) => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(Boolean(projectId));
  const [loadError, setLoadError] = useState('');
  const [updateError, setUpdateError] = useState('');
  const [updatingPreferences, setUpdatingPreferences] = useState({});

  const loadPreferences = useCallback(async () => {
    if (!projectId) {
      setEvents([]);
      setLoadError('');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setLoadError('');

    try {
      const response = await apiClient.get(getPreferencesPath(projectId));
      const preferences = getResponseData(response);
      setEvents(Array.isArray(preferences)
        ? preferences.filter(({ eventKey }) => NOTIFICATION_SETTINGS_LABELS.events[eventKey])
        : []);
    } catch (error) {
      setLoadError(error?.response?.data?.message || NOTIFICATION_SETTINGS_LABELS.loadError);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const updatePreference = async (eventKey, channel, enabled) => {
    if (!projectId) return;

    const preferenceKey = getPreferenceKey(eventKey, channel);
    setUpdateError('');
    setUpdatingPreferences((current) => ({ ...current, [preferenceKey]: true }));

    try {
      await apiClient.put(getPreferencesPath(projectId), { eventKey, channel, enabled });
      setEvents((currentEvents) => currentEvents.map((event) => (
        event.eventKey === eventKey
          ? {
            ...event,
            channels: event.channels.map((preference) => (
              preference.channel === channel ? { ...preference, enabled } : preference
            )),
          }
          : event
      )));
    } catch (error) {
      setUpdateError(error?.response?.data?.message || NOTIFICATION_SETTINGS_LABELS.updateError);
    } finally {
      setUpdatingPreferences((current) => ({ ...current, [preferenceKey]: false }));
    }
  };

  const categories = groupEventsByCategory(events);

  if (isLoading) {
    return <p className={styles.status}>{NOTIFICATION_SETTINGS_LABELS.loading}</p>;
  }

  if (loadError) {
    return (
      <div className={`${styles.status} ${styles.error}`} role="alert">
        <p>{loadError}</p>
        <button type="button" className={styles.retryButton} onClick={loadPreferences}>
          {NOTIFICATION_SETTINGS_LABELS.retry}
        </button>
      </div>
    );
  }

  return (
    <section className={styles.settings} aria-labelledby="notification-settings-heading">
      <header className={styles.header}>
        <h2 id="notification-settings-heading">{NOTIFICATION_SETTINGS_LABELS.heading}</h2>
        <p>{NOTIFICATION_SETTINGS_LABELS.description}</p>
      </header>

      {updateError && <p className={styles.updateError} role="alert">{updateError}</p>}

      {events.length === 0 ? (
        <p className={styles.status}>{NOTIFICATION_SETTINGS_LABELS.empty}</p>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">{NOTIFICATION_SETTINGS_LABELS.event}</th>
                {CHANNELS.map((channel) => (
                  <th key={channel} scope="col">{NOTIFICATION_SETTINGS_LABELS.channels[channel]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(categories).map(([category, categoryEvents]) => (
                <React.Fragment key={category}>
                  <tr className={styles.category}>
                    <th colSpan={CHANNELS.length + 1} scope="rowgroup">{category}</th>
                  </tr>
                  {categoryEvents.map((event) => {
                    const preferences = new Map(event.channels.map((preference) => [preference.channel, preference]));

                    return (
                      <tr key={event.eventKey}>
                        <th scope="row">{NOTIFICATION_SETTINGS_LABELS.events[event.eventKey]}</th>
                        {CHANNELS.map((channel) => {
                          const preference = preferences.get(channel);
                          const preferenceKey = getPreferenceKey(event.eventKey, channel);

                          return (
                            <td key={channel}>
                              {preference ? (
                                <SwitchComponent
                                  aria-label={`${NOTIFICATION_SETTINGS_LABELS.events[event.eventKey]} ${NOTIFICATION_SETTINGS_LABELS.channels[channel]}`}
                                  checked={preference.enabled}
                                  disabled={Boolean(updatingPreferences[preferenceKey])}
                                  onChange={(inputEvent) => updatePreference(event.eventKey, channel, inputEvent.target.checked)}
                                  size="small"
                                />
                              ) : (
                                <span className={styles.unavailable}>{NOTIFICATION_SETTINGS_LABELS.unavailable}</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default NotificationSettingsTable;
